import { toSnakeCase } from "@/src/utils/analyticUtils.js";
import { fetchCountryOptions } from "@/src/utils/dynamicFormUtils.js";
import {
  priorityOptions,
  dealTypeOptions,
  companySizeOptions,
} from "@/src/constants/filterConstants.js";
import {
  booleanOptions,
  ATTRIBUTE_TYPES,
  standardObjectStatusFields,
} from "@/src/constants/crmConstants.js";
import {
  analyticsHardcodedSchemaObjects,
  propertySlugs,
  objectSlugs,
  propertyTypes,
  standardObjects,
  scoreOptions,
  excludedUserSlugMatches,
  excludedTeamSlugMatches,
} from "@/src/constants/objectConstants.js";
import store from "@/src/store/index.js";
import CRMService from "@/src/api/CRMService.js";
import CallService from "@/src/api/CallService.js";
import DashboardService from "@/src/api/DashboardService.js";

async function loadPropertyOptions(prop, objectSlug) {
  try {
    const propType = String(prop.type || "").toLowerCase();
    const propSlug = String(prop.slug || "").toLowerCase();
    const propName = String(prop.name || "").toLowerCase();

    if (
      propType === ATTRIBUTE_TYPES.BOOLEAN ||
      propType === propertyTypes.boolean
    ) {
      return booleanOptions.map((opt) => ({
        id: opt.value,
        name: opt.label,
        value: opt.value,
      }));
    }

    if (
      !excludedUserSlugMatches.includes(propSlug) &&
      (propType === ATTRIBUTE_TYPES.USER ||
        propType === propertyTypes.user ||
        propSlug.includes(propertySlugs.owner) ||
        propSlug.includes(propertySlugs.user))
    ) {
      let users = store.getters["users/usersWithDeleted"];

      if (!users || users.length === 0) {
        users = store.getters["users/users"];
      }

      if (users && Array.isArray(users)) {
        return users.map((user) => ({
          id: user.id,
          name: user.name || user.email,
          isDeleted: user.isDeleted || false,
        }));
      }
      return [];
    }

    if (propType === propertyTypes.tags || propSlug === propertySlugs.tags) {
      try {
        const response = await CRMService.getTagsForEntity(objectSlug);
        if (response?.success && response?.tags) {
          return response.tags.map((tag) => ({
            id: tag.id || tag.name,
            name: tag.name,
          }));
        }
      } catch (error) {
        console.error(`Error fetching tags for ${objectSlug}:`, error);
      }
      return [];
    }

    if (
      propSlug === propertySlugs.country ||
      propSlug === propertySlugs.countries ||
      propName === propertySlugs.country
    ) {
      if (prop.options && prop.options.length > 0) {
        return prop.options.map((opt) => {
          if (typeof opt === "string") {
            return { id: opt, name: opt };
          }
          return {
            id: opt.id || opt.value || opt,
            name: opt.name || opt.label || opt.value || opt,
          };
        });
      }

      try {
        const opts = await fetchCountryOptions();
        return (opts || []).map((o) => ({
          id: o.value,
          name: o.label,
        }));
      } catch (error) {
        console.error("Error fetching country options:", error);
      }
      return [];
    }

    if (
      !excludedTeamSlugMatches.includes(propSlug) &&
      (propSlug.includes(propertySlugs.team) ||
        propName.includes(propertySlugs.team))
    ) {
      try {
        const response = await DashboardService.fetchTeams();
        if (response?.success && response?.teams) {
          return response.teams.map((team) => ({
            id: team.id,
            name: team.name,
          }));
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
      return [];
    }

    const isLeadsObject = objectSlug === objectSlugs.leads;
    if (
      isLeadsObject &&
      (propSlug.includes(propertySlugs.source) ||
        propName.includes(propertySlugs.source))
    ) {
      try {
        const response = await DashboardService.getLeadSourceList();
        if (response?.success && response?.sources) {
          return response.sources.map((source) => ({
            id: source.id || source.name || source,
            name: source.name || source,
          }));
        }
      } catch (error) {
        console.error("Error fetching lead sources:", error);
      }
      return [];
    }

    if (
      isLeadsObject &&
      (propSlug === propertySlugs.score || propName === propertySlugs.score)
    ) {
      return scoreOptions.map((opt) => ({
        id: opt.id,
        name: opt.name,
      }));
    }


    const isCallsObject = objectSlug === objectSlugs.calls;
    if (
      isCallsObject &&
      (propSlug === propertySlugs.disposition ||
        propName === propertySlugs.disposition)
    ) {
      try {
        const response = await CallService.fetchCallDispositions({
          page: 1,
          page_size: 100,
        });
        if (response?.data && Array.isArray(response.data)) {
          return response.data.map((d) => ({
            id: d.disposition || d.name,
            name: d.disposition || d.name,
          }));
        }
      } catch (error) {
        console.error("Error fetching call dispositions:", error);
      }
      return [];
    }

    const isDealObject = [objectSlugs.deals, objectSlugs.deal].includes(
      objectSlug,
    );
    if (
      isDealObject &&
      (propSlug === propertySlugs.pipelineId ||
        propSlug === propertySlugs.pipeline)
    ) {
      if (prop.structured_options && prop.structured_options.length > 0) {
        return prop.structured_options.map((opt) => ({
          id: Number(opt.id),
          name: opt.label,
        }));
      }
      try {
        const response = await store.dispatch(
          "pipelines/fetchPipelines",
          {},
          { root: true },
        );
        if (response?.success && response?.pipelines) {
          return response.pipelines.map((p) => ({
            id: p.id,
            name: p.name,
          }));
        }
      } catch (error) {
        console.error("Error fetching pipelines:", error);
      }
      return [];
    }

    if (
      isDealObject &&
      (propSlug === propertySlugs.pipelineStageId ||
        propSlug === propertySlugs.dealStage)
    ) {
      if (prop.structured_options && prop.structured_options.length > 0) {
        return prop.structured_options.map((opt) => ({
          id: Number(opt.id),
          name: opt.label,
        }));
      }
      try {
        const response = await store.dispatch(
          "pipelines/fetchPipelineStages",
          {},
          { root: true },
        );
        if (response?.success && response?.pipeline_stages) {
          return response.pipeline_stages.map((s) => ({
            id: s.id,
            name: s.name,
          }));
        }
      } catch (error) {
        console.error("Error fetching pipeline stages:", error);
      }
      return [];
    }


    if (
      (propSlug.includes(propertySlugs.company) ||
        propName.includes(propertySlugs.company) ||
        propSlug === propertySlugs.currentCompany) &&
      (!prop.options || prop.options.length === 0)
    ) {
      try {
        const response = await CRMService.searchDynamicObjects({
          object_type: 'companies',
          search_query: '',
          page: 1,
          page_size: 100,
        });
        if (response?.success) {
          return (response.results || []).map((company) => {
            const attrs = company?.attributes || {};
            return {
              id: parseInt(company?.id, 10),
              name: attrs?.name?.value || attrs?.name || null,
            };
          });
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
      return [];
    }


    if (propType === propertyTypes.relationship) {
      return [];
    }

    if (
      (propType === ATTRIBUTE_TYPES.SELECT ||
        propType === propertyTypes.select ||
        propType === ATTRIBUTE_TYPES.MULTI_SELECT ||
        propType === propertyTypes.multiSelect) &&
      prop.options &&
      Array.isArray(prop.options) &&
      prop.options.length > 0
    ) {
      return prop.options.map((opt) => {
        if (typeof opt === "string") {
          return { id: opt, name: opt };
        }
        return {
          id: opt.id || opt.value || opt,
          name: opt.name || opt.label || opt.value || opt,
        };
      });
    }

    return [];
  } catch (error) {
    console.error(`Error loading options for property ${prop.slug}:`, error);
    return [];
  }
}

export default {
  namespaced: true,

  state: {
    objects: [],
    loadingObjects: false,
    fetchObjectsPromise: null,
    objectsFetched: false,
  },

  mutations: {
    SET_OBJECTS(state, value) {
      state.objects = value;
    },

    SET_LOADING_OBJECTS(state, value) {
      state.loadingObjects = value;
    },

    SET_FETCH_OBJECTS_PROMISE(state, value) {
      state.fetchObjectsPromise = value;
    },

    SET_OBJECTS_FETCHED(state, value) {
      state.objectsFetched = value;
    },
  },

  actions: {
    async fetchObjects({ commit, state }) {
      if (state.fetchObjectsPromise) {
        return state.fetchObjectsPromise;
      }

      if (state.objects && state.objects.length > 0) {
        commit("SET_OBJECTS_FETCHED", true);
        return Promise.resolve();
      }

      const fetchPromise = (async () => {
        try {
          commit("SET_LOADING_OBJECTS", true);

          const response = await CRMService.fetchDynamicCRMListObjects();

          if (response?.success && response?.objects) {
            const objectsList = [
              ...analyticsHardcodedSchemaObjects?.simple,
              ...response.objects,
            ];

            const objectsWithProperties = await Promise.all(
              objectsList.map(async (obj) => {
                try {
                  const schemaResponse = await CRMService.fetchSchemaObject(
                    obj.slug,
                    obj.service || null,
                  );

                  if (
                    schemaResponse?.success &&
                    schemaResponse?.schema?.attributes
                  ) {
                    const propertiesWithOptions = await Promise.all(
                      schemaResponse.schema.attributes.map(async (attr) => {
                        const options = await loadPropertyOptions(
                          {
                            type: attr.type,
                            slug: attr.slug,
                            name: attr.name,
                            options: attr.options || [],
                            structured_options: attr.structured_options || [],
                          },
                          obj.slug,
                        );

                        return {
                          id: attr.id,
                          slug: attr.slug,
                          name: attr.name,
                          label: attr.name,
                          type: attr.type,
                          dataType: attr.type,
                          options: options,
                          is_multi_value: attr.is_multi_value || false,
                          is_required: attr.is_required || false,
                          is_unique: attr.is_unique || false,
                          is_primary: attr.is_primary || false,
                          is_system: attr.is_system || false,
                          is_internal: attr.is_internal || false,
                        };
                      }),
                    );

                    return {
                      id: obj.id,
                      name: obj.name,
                      plural_name: obj.plural_name,
                      slug: obj.slug,
                      icon: obj.icon,
                      service: schemaResponse.schema.object?.service || obj.service || null,
                      is_system: obj.is_system || false,
                      properties: propertiesWithOptions,
                      relationships: schemaResponse.schema.relationships || [],
                      object: schemaResponse.schema.object || null,
                    };
                  }

                  return {
                    id: obj.id,
                    name: obj.name,
                    plural_name: obj.plural_name,
                    slug: obj.slug,
                    icon: obj.icon,
                    service: obj.service || null,
                    is_system: obj.is_system || false,
                    properties: [],
                    relationships: [],
                    object: null,
                  };
                } catch (error) {
                  console.error(
                    `Error fetching schema for ${obj.slug}:`,
                    error,
                  );
                  return {
                    id: obj.id,
                    name: obj.name,
                    plural_name: obj.plural_name,
                    slug: obj.slug,
                    icon: obj.icon,
                    service: obj.service || null,
                    is_system: obj.is_system || false,
                    properties: [],
                    relationships: [],
                    object: null,
                  };
                }
              }),
            );

            commit("SET_OBJECTS", objectsWithProperties);
          }
        } catch (error) {
          console.error("An error occurred while fetching objects: ", error);
        } finally {
          commit("SET_LOADING_OBJECTS", false);
          commit("SET_FETCH_OBJECTS_PROMISE", null);
          commit("SET_OBJECTS_FETCHED", true);
        }
      })();

      commit("SET_FETCH_OBJECTS_PROMISE", fetchPromise);
      return fetchPromise;
    },

    async refreshObjects({ commit, dispatch }) {
      commit("SET_OBJECTS", []);
      commit("SET_FETCH_OBJECTS_PROMISE", null);
      await dispatch("fetchObjects");
    },

    setObjects({ commit }, value) {
      commit("SET_OBJECTS", value);
    },

    setLoadingObjects({ commit }, value) {
      commit("SET_LOADING_OBJECTS", value);
    },
  },

  getters: {
    objects: (state) => state.objects,
    loadingObjects: (state) => state.loadingObjects,
    objectsFetched: (state) => state.objectsFetched,
    getAttributesBySlugOrId: (state) => (slugOrId) => {
      const obj = state.objects.find(
        (o) => o.slug === slugOrId || o.id === slugOrId,
      );
      return obj?.properties || [];
    },
    getObjectBySlugOrId: (state) => (slugOrId) => {
      return state.objects.find(
        (o) => o.slug === slugOrId || o.id === slugOrId,
      );
    },
  },
};
